/**
 * The ISO week the user is in — VH-138.
 *
 * `GET /weekly-programs/week/{year}/{week}` speaks ISO 8601 weeks, so this has
 * to agree with it exactly or the phone offers the wrong week's sessions.
 *
 * Two things make this less trivial than it looks, and both were called out in
 * the spec as edge cases:
 *
 *   - The ISO year is not always the calendar year. 1 January 2027 falls in
 *     week 53 of 2026, so returning `getFullYear()` alongside an ISO week
 *     number would ask the API for a week that does not exist.
 *   - The week must be derived from the user's LOCAL date. Using UTC would
 *     move a Sunday evening in Zurich into the following week.
 *
 * Implemented the standard way: shift to the Thursday of the current week —
 * ISO defines a week's year by which year its Thursday falls in — then count
 * weeks from the first Thursday of that year.
 */

/**
 * @param {Date} [now] injectable for tests; defaults to the local "now"
 * @returns {{year: number, week: number}} ISO year and 1-53 week number
 */
export const currentIsoWeek = (now = new Date()) => {
  // Work in UTC on a copy built from LOCAL date parts. Taking the local
  // calendar date first is what keeps a late Sunday in Zurich in its own week;
  // doing the arithmetic in UTC afterwards avoids DST shifting a day.
  const date = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))

  // ISO weeks run Monday(1)..Sunday(7); JS gives Sunday as 0.
  const dayNumber = date.getUTCDay() || 7
  // Move to this week's Thursday, which defines the ISO year.
  date.setUTCDate(date.getUTCDate() + 4 - dayNumber)

  const year = date.getUTCFullYear()
  const firstThursday = new Date(Date.UTC(year, 0, 4))
  const firstDayNumber = firstThursday.getUTCDay() || 7
  firstThursday.setUTCDate(firstThursday.getUTCDate() + 4 - firstDayNumber)

  const week = 1 + Math.round((date - firstThursday) / (7 * 24 * 60 * 60 * 1000))
  return { year, week }
}

/**
 * The Monday of a given ISO week, as a UTC date — VH-154.
 *
 * Inverse of {@link currentIsoWeek}. Needed because "the week before week 1"
 * is not "week 0": it is week 52 or 53 of the previous ISO year, and which one
 * depends on the year. Doing the arithmetic in dates rather than on the week
 * number is what makes that fall out correctly instead of being special-cased.
 */
export const isoWeekMonday = (year, week) => {
  const jan4 = new Date(Date.UTC(year, 0, 4))
  const jan4Day = jan4.getUTCDay() || 7
  // Monday of ISO week 1 — the week containing 4 January, by definition.
  const week1Monday = new Date(jan4)
  week1Monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1)
  const monday = new Date(week1Monday)
  monday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7)
  return monday
}

/**
 * Move `delta` ISO weeks from {year, week} — VH-154.
 *
 * Reto could not reach week 34 from week 33 on his phone: the reporting page
 * was pinned to the current week with no way to move. Crossing a year boundary
 * has to land on 52 or 53 correctly, so this shifts the actual Monday and
 * re-derives the ISO week from it rather than adding to the number — the naive
 * version produces "week 0" and is caught by the tests.
 */
export const shiftIsoWeek = (year, week, delta) => {
  const monday = isoWeekMonday(year, week)
  monday.setUTCDate(monday.getUTCDate() + delta * 7)
  // Re-derive through the same rules the API speaks. `currentIsoWeek` reads
  // LOCAL date parts, so hand it a date whose local parts are this Monday.
  return currentIsoWeek(
    new Date(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate())
  )
}

export default currentIsoWeek
